import type { IQuickIntroRepository, QuickIntroPromptState } from '@/core/domain/repositories/IQuickIntroRepository';

/** Coordinates an invitation awaiting a decision, never customer-requested playback. */
export class QuickIntroPromptLease {
  private active: {
    releaseBrowser: () => void;
    releaseAccount?: () => void;
    accountId?: string;
    token?: string;
    deadline: number;
    expire: () => void;
  } | null = null;
  private expiryTimer?: ReturnType<typeof setTimeout>;
  private renewalTimer?: ReturnType<typeof setTimeout>;

  constructor(private readonly repository: IQuickIntroRepository | undefined,
    private readonly now: () => number) {}

  static deadline(response: QuickIntroPromptState, requestedAt: number): number {
    const duration = Date.parse(response.prompt_expires_at ?? '') - Date.parse(response.server_time);
    return !response.released && Number.isFinite(requestedAt) && requestedAt >= 0 &&
      Number.isFinite(duration) && duration > 0 && duration <= 90000
      ? requestedAt + duration : NaN;
  }

  start(releaseBrowser: () => void, expire: () => void,
    account?: { accountId: string; token: string; deadline: number; release: () => void }): void {
    this.release();
    this.active = { releaseBrowser, expire, deadline: account?.deadline ?? Infinity,
      accountId: account?.accountId, token: account?.token, releaseAccount: account?.release };
    if (account) this.schedule();
  }

  private schedule(): void {
    const active = this.active;
    if (!active?.accountId) return;
    if (this.expiryTimer !== undefined) clearTimeout(this.expiryTimer);
    if (this.renewalTimer !== undefined) clearTimeout(this.renewalTimer);
    const remaining = active.deadline - this.now();
    if (!Number.isFinite(remaining) || remaining <= 0) { this.expire(); return; }
    this.expiryTimer = setTimeout(() => this.check(), remaining);
    this.renewalTimer = setTimeout(() => { void this.renew(active); }, Math.min(30000, remaining));
  }

  /** Also called on visibility/focus events, where background timers may have slept. */
  check(): void {
    if (this.active?.accountId && (!Number.isFinite(this.now()) || this.now() < 0 || this.now() >= this.active.deadline)) this.expire();
  }

  private expire(): void {
    const active = this.active;
    this.release();
    active?.expire();
  }

  private async renew(active: NonNullable<QuickIntroPromptLease['active']>): Promise<void> {
    this.check();
    if (this.active !== active || !active.accountId || !active.token || !this.repository) return;
    const requestedAt = this.now();
    try {
      const response = await this.repository.updateQuickIntroPrompt(active.accountId, active.token, 'renew');
      // Play/close may occur while the request is in flight. Never revive that prompt.
      if (this.active !== active) return;
      this.check();
      if (this.active !== active) return;
      const deadline = QuickIntroPromptLease.deadline(response, requestedAt);
      if (!Number.isFinite(deadline) || deadline <= this.now()) { this.expire(); return; }
      active.deadline = deadline;
      this.schedule();
    } catch {
      // A temporary outage cannot revoke the remaining granted time or stop playback.
      // The original expiry timer remains authoritative; retry at the normal cadence.
      if (this.active === active) this.schedule();
    }
  }

  release(): void {
    const active = this.active;
    this.active = null;
    if (this.expiryTimer !== undefined) clearTimeout(this.expiryTimer);
    if (this.renewalTimer !== undefined) clearTimeout(this.renewalTimer);
    this.expiryTimer = undefined;
    this.renewalTimer = undefined;
    active?.releaseBrowser();
    active?.releaseAccount?.();
  }
}
